/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import { useRouter } from 'next/router';
import React, {
    Fragment,
    FunctionComponent,
    MouseEvent,
    ReactNode,
    useState,
} from 'react';
import styled from 'styled-components';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { WrappedLink } from '@toucan/client/global/nextjs';

import { Link, LinkColor } from '@toucan/client/shared/components/link';

import { styles } from './sidebar-navigation.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SidebarNavLink {
    /**
     * A react node to show for the content of the link
     */
    content: ReactNode;

    /**
     * Link URL
     */
    href?: string;

    /**
     * Path that will be rendered by the browser
     */
    as?: string;

    /**
     * An icon to show for the nav item
     */
    icon?: ReactNode;

    /**
     * Sub menu links
     */
    children?: SidebarNavLink[];

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SidebarNavigationProps {
    /**
     * An array of navigation links for the sidebar
     */
    navigation: SidebarNavLink[];

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

export type StyledSidebarNavigationProps = SidebarNavigationProps & {
    activeParentIndex?: number;
};

const StyledSidebarNavigation = styled('nav').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const SidebarNavigation: FunctionComponent<SidebarNavigationProps> = ({
    navigation,
    ...otherProps
}: SidebarNavigationProps) => {
    const router = useRouter();
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const [mobileActiveParentIndex, setMobileActiveParentIndex] =
        useState<number>();

    function handleParentItemClick(event: MouseEvent<HTMLElement>) {
        const listItem = (event.target as HTMLElement).closest('li');
        const depth = parseInt(listItem?.getAttribute('data-depth') || '', 10);
        const index = parseInt(listItem?.getAttribute('data-index') || '', 10);

        if (depth === 0) {
            setMobileActiveParentIndex((currentIndex) => {
                return index === currentIndex ? undefined : index;
            });
        }
    }

    /**
     * Finds the root menu item that should be open
     */
    function findActiveParentIndex(
        links: SidebarNavLink[],
        pathname: string
    ): number | false {
        return (
            links.findIndex((link) => {
                return link.children?.some(({ href }) => href === pathname);
            }) ?? false
        );
    }

    const desktopActiveParentIndex = findActiveParentIndex(
        navigation,
        router.pathname
    );

    const activeParentIndex = isSmallTablet
        ? mobileActiveParentIndex
        : desktopActiveParentIndex;

    function renderLink(
        link: SidebarNavLink,
        pathname: string,
        isMobile: boolean,
        depth: number
    ) {
        const { icon, content, children, 'data-testid': testId } = link;
        const iconNode = link.icon ? (
            <div className="sidebar-navigation-icon">{icon}</div>
        ) : null;

        let className = 'sidebar-navigation-link';
        let asPath = link.as;
        let href = link.href;

        const isActiveLink = href === pathname;

        if (isActiveLink) {
            className += ' sidebar-navigation-link--active';
        }

        // sets the href to the value of the first child if there is none
        if (!isMobile && !href && children?.length !== 0) {
            const firstChildWithHref = children?.find(({ href }) => {
                return !!href;
            });

            asPath = firstChildWithHref?.as;
            href = firstChildWithHref?.href;
        }

        if (href) {
            return (
                <WrappedLink
                    className={className}
                    linkColor={LinkColor.NAVIGATION}
                    role="menuitem"
                    href={href || ''}
                    as={asPath}
                    data-testid={testId}
                >
                    {iconNode} {content}
                </WrappedLink>
            );
        }

        if (Array.isArray(children) && children.length !== 0) {
            return (
                <Link
                    className={className}
                    linkColor={LinkColor.NAVIGATION}
                    role="menuitem"
                    onClick={handleParentItemClick}
                    data-testid={testId}
                >
                    {iconNode} {content}
                </Link>
            );
        }

        return (
            <span className={className} data-testid={testId}>
                {iconNode} {content}
            </span>
        );
    }

    function renderNavigationNodes(
        links: SidebarNavLink[],
        pathname: string,
        isMobile: boolean,
        depth: number = 0
    ) {
        return links.map((link, i) => {
            const key = `${depth}-${i}`;
            const className = 'sidebar-navigation-list-item';

            const listItemContents = link.children ? (
                <Fragment>
                    {renderLink(link, pathname, isMobile, depth)}
                    <ul className="sidebar-navigation-sub-menu">
                        {renderNavigationNodes(
                            link.children,
                            pathname,
                            isMobile,
                            depth + 1
                        )}
                    </ul>
                </Fragment>
            ) : (
                renderLink(link, pathname, isMobile, depth)
            );

            return (
                <li
                    key={key}
                    className={className}
                    data-depth={depth}
                    data-index={i}
                >
                    {listItemContents}
                </li>
            );
        });
    }

    return (
        <StyledSidebarNavigation
            navigation={navigation}
            activeParentIndex={
                typeof activeParentIndex === 'number'
                    ? activeParentIndex
                    : undefined
            }
            {...otherProps}
        >
            <ul className="sidebar-navigation-menu">
                {renderNavigationNodes(
                    navigation,
                    router.pathname,
                    isSmallTablet
                )}
            </ul>
        </StyledSidebarNavigation>
    );
};
